import React, { useRef, useEffect, useState, forwardRef, useImperativeHandle } from "react";
import { Box, CircularProgress, Popper, Typography } from '@mui/material';
import { FaExpand, FaCompress } from "react-icons/fa6";
import { FaPlayCircle, FaPauseCircle, FaCamera } from "react-icons/fa";
import NaviBox from './NaviBox'




const SCALE_BY = 1.05;
const MAX_SCALE = 6;
const MIN_SCALE = 0.1;
const PANSTEP = 40;

const FixedBox = {
    position: 'absolute',
    top: 0, bottom: 0,
    left: 0, right: 0,
    margin: 'auto',
    zIndex: 1999,
    userSelect: "none",
}
const FlexBox = {
    display: 'flex',
    gap: 2, alignItems: 'center', justifyContent: 'center'
}

const VideoViewer = forwardRef(({
    src, type = "video",
    showPrev = true,
    showNext = true,
    icons = {},
    prevPath = "",
    nextPath = "",
    onChange = (data) => { },
    label = "",
}, ref) => {
    useImperativeHandle(ref, () => ({
        getSnapshot: () => {
            const el = mediaRef.current;
            if (!el) return null;

            if (el.tagName === "IMG" && !el.complete) return null;
            if (el.tagName === "VIDEO" && el.readyState < 2) return null;

            const canvas = document.createElement("canvas");
            const ctx = canvas.getContext("2d");

            const width = el.tagName === "IMG" ? el.naturalWidth : el.videoWidth;
            const height = el.tagName === "IMG" ? el.naturalHeight : el.videoHeight;

            if (!width || !height) return null;

            canvas.width = width;
            canvas.height = height;

            try {
                ctx.drawImage(el, 0, 0, width, height);
                return canvas.toDataURL("image/png");
            } catch (e) {
                console.error("Failed to snapshot media:", e);
                return null;
            }
        }
    }));
    const containerRef = useRef(null);
    const mediaRef = useRef(null);
    const mediaRecorderRef = useRef(null);
    const recordedChunks = useRef([]);
    const lastTouchDistance = useRef(null);

    const [scale, setScale] = useState(1);
    const [position, setPosition] = useState({ x: 0, y: 0 });
    const [dragging, setDragging] = useState(false);
    const [last, setLast] = useState({ x: 0, y: 0 });

    const [duration, setDuration] = useState(0);
    const [currentTime, setCurrentTime] = useState(0);
    const [loader, setLoader] = useState(true);
    const [popup, setPopup] = useState({});
    const [initData, setInitData] = useState({});

    const [isFullscreen, setIsFullscreen] = useState(false);

    const [showControls, setShowControls] = useState(true);
    const hideTimeoutRef = useRef(null);

    const handleInteraction = () => {
        setShowControls(true);
        if (hideTimeoutRef.current) clearTimeout(hideTimeoutRef.current);
        hideTimeoutRef.current = setTimeout(() => setShowControls(false), 3000);
    };

    useEffect(() => {
        handleInteraction();
        return () => {
            if (hideTimeoutRef.current) clearTimeout(hideTimeoutRef.current);
        };
    }, []);

    useEffect(() => {
        const handleChange = () => {
            setIsFullscreen(!!document.fullscreenElement);
        };
        document.addEventListener("fullscreenchange", handleChange);
        return () => {
            document.removeEventListener("fullscreenchange", handleChange);
        };
    }, []);

    // Workaround for passive event listener warning on wheel events
    useEffect(() => {
        const container = containerRef.current;
        if (!container) return;

        const preventDefaultWheel = (e) => e.preventDefault();
        // passive: false is required to allow preventDefault
        container.addEventListener('wheel', preventDefaultWheel, { passive: false });

        return () => {
            container.removeEventListener('wheel', preventDefaultWheel);
        };
    }, []);

    // ---------------- FIT TO SCREEN ----------------
    useEffect(() => {
        const media = mediaRef.current;
        const container = containerRef.current;
        if (!media || !container || !src || (typeof src === "string" && (src.includes("undefined") || src.includes("null")))) return;

        const fit = () => {
            const cw = container.clientWidth;
            const ch = container.clientHeight;

            const mw =
                type === "video" ? media.videoWidth : media.naturalWidth;
            const mh =
                type === "video" ? media.videoHeight : media.naturalHeight;

            const fitScale = Math.max(cw / mw, ch / mh);

            const quality = getQuality(mw, mh)
            console.log("RESOLTION", quality)

            setScale(fitScale);
            setPosition({ x: 0, y: 0 });
            setLoader(false)
            setInitData({ fitScale, x: 0, y: 0, quality })
        };

        if (type === "video") {
            media.src = src
            media.onloadedmetadata = () => {
                setDuration(media.duration || 0);
                fit();
            };
            media.ontimeupdate = () =>
                setCurrentTime(media.currentTime);
        } else {
            media.src = src
            media.onload = fit;
        }
    }, [src, type]);

    // ---------------- CLAMP ------------------------
    const clamp = (x, y, s) => {
        const container = containerRef.current;
        const media = mediaRef.current;
        if (!container || !media) return { x, y };

        const cw = container.clientWidth;
        const ch = container.clientHeight;

        const mw =
            type === "video" ? media.videoWidth : media.naturalWidth;
        const mh =
            type === "video" ? media.videoHeight : media.naturalHeight;

        const sw = mw * s;
        const sh = mh * s;

        const maxX = Math.max(0, (sw - cw) / 2);
        const maxY = Math.max(0, (sh - ch) / 2);

        return {
            x: Math.min(maxX, Math.max(-maxX, x)),
            y: Math.min(maxY, Math.max(-maxY, y)),
        };
    };

    const clampScale = (s) => {
        const container = containerRef.current;
        const media = mediaRef.current;
        if (!container || !media) return s;

        const ch = container.clientHeight;

        const mh =
            type === "video" ? media.videoHeight : media.naturalHeight;

        // Minimum: fit to height
        const minScale = ch / mh;

        // Maximum: allow zoom up to 3x of original height (customize)
        const maxScale = (ch / mh) * 3;

        return Math.max(minScale, Math.min(maxScale, s));
    };

    // ---------------- ZOOM ----------------
    const handleWheel = (e) => {
        const dir = e.deltaY > 0 ? -1 : 1;

        applyZoom(dir, e.clientX, e.clientY);
    };

    const handleZoom = (type) => {
        const rect = containerRef.current.getBoundingClientRect();

        const centerX = rect.left + rect.width / 2;
        const centerY = rect.top + rect.height / 2;

        const dir = type === "in" ? 1 : -1;

        applyZoom(dir, centerX, centerY);
    };

    const applyZoom = (dir, clientX, clientY) => {
        const rect = containerRef.current.getBoundingClientRect();

        const mx = clientX - rect.left - rect.width / 2;
        const my = clientY - rect.top - rect.height / 2;

        let newScale = dir > 0 ? scale * SCALE_BY : scale / SCALE_BY;

        newScale = clampScale(newScale);
        newScale = Math.max(MIN_SCALE, Math.min(MAX_SCALE, newScale));

        const ratio = newScale / scale;

        const newX = position.x - mx * (ratio - 1);
        const newY = position.y - my * (ratio - 1);

        const clamped = clamp(newX, newY, newScale);

        setScale(newScale);
        setPosition(clamped);
    };

    // ---------------- DRAG ----------------
    const onDown = (e) => {
        setDragging(true);
        setLast({ x: e.clientX, y: e.clientY });
    };

    const onMove = (e) => {
        if (!dragging) return;

        const dx = e.clientX - last.x;
        const dy = e.clientY - last.y;

        applyMove(dx, dy); // 🔥 reuse

        setLast({ x: e.clientX, y: e.clientY });
    };

    const onUp = () => setDragging(false);

    // ---------------- PINCH ZOOM ----------------
    const handleTouchMove = (e) => {
        if (e.touches.length !== 2) return;

        const t1 = e.touches[0];
        const t2 = e.touches[1];

        const dist = Math.hypot(
            t2.clientX - t1.clientX,
            t2.clientY - t1.clientY
        );

        if (!lastTouchDistance.current) {
            lastTouchDistance.current = dist;
            return;
        }

        let newScale = scale * (dist / lastTouchDistance.current);
        newScale = Math.max(MIN_SCALE, Math.min(MAX_SCALE, newScale));

        const clamped = clamp(position.x, position.y, newScale);

        setScale(newScale);
        setPosition(clamped);

        lastTouchDistance.current = dist;
    };

    const handleTouchEnd = () => {
        lastTouchDistance.current = null;
    };

    // ---------------- RECORDING ----------------
    const startRecording = () => {
        if (type !== "video") return;

        const stream = mediaRef.current.captureStream();
        const recorder = new MediaRecorder(stream);

        recordedChunks.current = [];

        recorder.ondataavailable = (e) => {
            if (e.data.size > 0)
                recordedChunks.current.push(e.data);
        };

        recorder.onstop = () => {
            const blob = new Blob(recordedChunks.current, {
                type: "video/webm",
            });
            const url = URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url;
            a.download = "recording.webm";
            a.click();
        };

        recorder.start();
        mediaRecorderRef.current = recorder;
    };

    const stopRecording = () => {
        mediaRecorderRef.current?.stop();
    };

    // ---------------- SCRUB ----------------
    const handleScrub = (e) => {
        const t = Number(e.target.value);
        mediaRef.current.currentTime = t;
        setCurrentTime(t);
    };

    const applyMove = (dx, dy) => {
        const clamped = clamp(
            position.x + dx,
            position.y + dy,
            scale
        );

        setPosition(clamped);
    };

    const handlePanZoom = (direction) => {
        if (direction == "reset") {
            setPosition({ x: initData?.x, y: initData?.y })
            setScale(initData?.fitScale)
            return
        }
        if (direction == "zoom-in" || direction == "zoom-out") {
            return handleZoom(direction == "zoom-in" ? "in" : 'out')
        }
        let dx = 0;
        let dy = 0;
        switch (direction) {
            case "up":
                dy = -PANSTEP;
                break;
            case "down":
                dy = PANSTEP;
                break;
            case "left":
                dx = -PANSTEP;
                break;
            case "right":
                dx = PANSTEP;
                break;
        }

        applyMove(dx, dy); // 🔥 SAME logic
    };
    const getQuality = (width, height) => {
        if (!width || !height) return "HD";

        // normalize portrait videos
        const w = Math.max(width, height);
        const h = Math.min(width, height);

        if (w >= 3840 && h >= 2160) return "UHD"; // 4K
        if (w >= 1920 && h >= 1080) return "FHD"; // 1080p

        return "HD";
    };

    const toggleFullscreen = () => {
        const el = containerRef.current;

        if (!document.fullscreenElement) {
            if (el.requestFullscreen) el.requestFullscreen();
            else if (el.webkitRequestFullscreen) el.webkitRequestFullscreen(); // Safari
            else if (el.msRequestFullscreen) el.msRequestFullscreen(); // IE
        } else {
            if (document.exitFullscreen) document.exitFullscreen();
            else if (document.webkitExitFullscreen) document.webkitExitFullscreen();
            else if (document.msExitFullscreen) document.msExitFullscreen();
        }
    };
    const onResolutionChange = (e) => {
        if (type == "image") return
        setLoader(true)
        const R = e.target.value
        const newUrl = src.endsWith("&") ? `resolution=${R}` : `&resolution=${R}`
        let url = `${src}${newUrl}`
        if (mediaRef.current && !mediaRef?.current?.paused) {
            mediaRef.current.pause()
        }
        mediaRef.current.src = url
        setInitData((prev) => ({ ...prev, quality: e.target.value }))
    }

    const view = {
        video: <>
            <video
                ref={mediaRef}
                src={src || undefined}
                crossOrigin="anonymous"
                muted
                playsInline
                // autoPlay
                style={{
                    position: "absolute",
                    top: "50%",
                    left: "50%",
                    transform: `translate(-50%, -50%) translate(${position.x}px, ${position.y}px) scale(${scale})`,
                    transformOrigin: "center",
                    transition: "transform 0.4s ease-out" // 👈 add this
                }}
            />
        </>,
        image: <>
            <img
                ref={mediaRef}
                alt=""
                // crossOrigin="anonymous"
                draggable={false}
                style={{
                    position: "absolute",
                    top: "50%",
                    left: "50%",
                    transform: `translate(-50%, -50%) translate(${position.x}px, ${position.y}px) scale(${scale})`,
                    transformOrigin: "center",
                    transition: "transform 0.4s ease-out" // 👈 add this

                }}
            />
        </>
    }

    const Buttons = {
        video: <Box sx={{ opacity: showControls ? 1 : 0, transition: 'opacity 0.3s', pointerEvents: showControls ? 'auto' : 'none' }}>
            {!loader && <Box sx={{ ...FixedBox, width: 60, height: 60 }}>
                {mediaRef.current?.paused ? <FaPlayCircle fontSize={48} className="pointer" onClick={() => mediaRef.current?.play()} /> : <FaPauseCircle className="pointer" fontSize={48} onClick={() => mediaRef.current?.pause()} />}
            </Box>}
        </Box>,
        image: <Box sx={{ opacity: showControls ? 1 : 0, transition: 'opacity 0.3s', pointerEvents: showControls ? 'auto' : 'none' }}>
            {showPrev && <Box onMouseEnter={(e) => { setPopup({ target: e.target, prev: true }) }} onMouseLeave={() => { setPopup({}) }}
                onClick={() => { setLoader(true); onChange({ type: "prev" }) }}
                sx={{ ...FixedBox, ...FlexBox, right: 'auto', left: 20, width: 40, height: 64 }}> {icons?.backward} </Box>}
            {showNext && <Box onMouseEnter={(e) => { setPopup({ target: e.target, next: true }) }} onMouseLeave={() => { setPopup({}) }}
                onClick={() => { setLoader(true); onChange({ type: "next" }) }}
                sx={{ ...FixedBox, ...FlexBox, right: 20, left: 'auto', width: 40, height: 64 }}> {icons?.forward} </Box>}
        </Box>
    }
    const takeSnapshot = (ref, filename = "snapshot.png", target = undefined) => {
        const el = ref.current;
        if (!el) return;

        if (el.tagName === "IMG" && !el.complete) return;
        if (el.tagName === "VIDEO" && el.readyState < 2) return;

        const canvas = document.createElement("canvas");
        const ctx = canvas.getContext("2d");

        const width = el.tagName === "IMG" ? el.naturalWidth : el.videoWidth;
        const height = el.tagName === "IMG" ? el.naturalHeight : el.videoHeight;

        canvas.width = width;
        canvas.height = height;

        ctx.drawImage(el, 0, 0, width, height);
        // if()

        const link = document.createElement("a");
        link.download = filename;
        link.href = canvas.toDataURL("image/png");
        link.click();
    };

    return (
        <>
            <div
                className="icon-filled-black"
                ref={containerRef}
                onWheel={(e) => { handleInteraction(); handleWheel(e); }}
                onMouseDown={(e) => { handleInteraction(); onDown(e); }}
                onMouseMove={(e) => { handleInteraction(); onMove(e); }}
                onMouseUp={onUp}
                onMouseLeave={(e) => { setShowControls(false); onUp(e); }}
                onTouchStart={handleInteraction}
                onTouchMove={(e) => { handleInteraction(); handleTouchMove(e); }}
                onTouchEnd={handleTouchEnd}
                style={{
                    width: "100%",
                    height: "100%",
                    background: "#111",
                    overflow: "hidden",
                    position: "relative",
                    cursor: dragging ? "grabbing" : "grab",
                    touchAction: "none",
                    userSelect: 'none'
                }}
            >
                <Box sx={{ ...FixedBox, top: 'auto', bottom: 'auto', left: 'auto', px: 2, py: 2, width: 160, height: 50, opacity: showControls ? 1 : 0, transition: 'opacity 0.3s', pointerEvents: showControls ? 'auto' : 'none' }}>
                    <Box sx={{ ...FlexBox, gap: 1, color: 'white' }}>
                        <Box sx={{
                            ...FlexBox,
                            px: 0.5, py: 0.1

                        }} onClick={() => takeSnapshot(mediaRef, `${label}_${Date().toLocaleLowerCase()}.png`)}>
                            <FaCamera className="pointer" color="white" size={18} />
                        </Box>
                        {type == "video" && <select style={{
                            background: '#000',
                            color: 'white', height: 24, width: 100,
                            paddingTop: 1, paddingBottom: 1
                        }} value={initData.quality || "HD"} onChange={(e) => { onResolutionChange(e) }}>
                            <option value="UHD">UHD</option>
                            <option value="FHD">FHD</option>
                            <option value="HD">HD</option>
                        </select>}
                        <Box className="pointer" sx={{ ...FlexBox, borderRadius: 1, border: '1px solid white', minWidth: 28, fontSize: 14, height: 24, background: '#000' }}>
                            {isFullscreen ? <FaCompress style={{ marginBottom: 2 }} onClick={() => toggleFullscreen()} /> :
                                <FaExpand onClick={() => toggleFullscreen()} />}
                        </Box>
                    </Box>
                </Box>

                {loader && <Box sx={{
                    ...FixedBox, FlexBox,
                    width: 70, height: 80, p: 1, borderRadius: 4,
                }}>
                    <Box sx={{ textAlign: 'center', }}>
                        <CircularProgress size={36} sx={{ color: "#ffcc33" }} />
                        <Typography fontSize={12} color="#ffcc33">Loading...</Typography>
                    </Box>
                </Box>}

                {Buttons[type]}
                {view[type]}

                <Box sx={{ ...FixedBox, top: 'auto', width: 140, height: 65, opacity: showControls ? 1 : 0, transition: 'opacity 0.3s', pointerEvents: showControls ? 'auto' : 'none' }}>
                    <NaviBox icons={icons} onChange={handlePanZoom} />
                </Box>

                {type == "video" && <>
                    <Box sx={{
                        ...FixedBox, top: 'auto', height: 20,
                        paddingLeft: { xs: 1, md: 4 },
                        paddingRight: { xs: 1, md: 4 },
                        opacity: showControls ? 1 : 0, transition: 'opacity 0.3s', pointerEvents: showControls ? 'auto' : 'none'
                    }}>
                        <input
                            className="custom-slider"
                            type="range"
                            min="0"
                            max={duration}
                            step="0.01"
                            value={currentTime}
                            onChange={handleScrub}
                            style={{ height: 10, background: '#ffcc33', width: "100%" }}
                        />
                    </Box>
                </>
                }
            </div>
            <Popper
                open={Boolean(popup?.target)}
                anchorEl={popup?.target}
                placement={popup?.next ? "left-start" : "right-start"}
                modifiers={[{ name: 'offset', options: { offset: [24, 0] } }]}
                sx={{ zIndex: 9999, borderRadius: 2, p: 0, display: { xs: 'none', sm: 'block' } }}
            >
                <Box sx={{ height: 120, borderRadius: 4, border: '1px solid white', overflow: 'hidden' }}>
                    {(showNext && popup?.next) && <img src={nextPath || undefined} height={120} />}
                    {(showPrev && popup?.prev) && <img src={prevPath || undefined} height={120} />}
                </Box>
            </Popper>

            {type === "video" && (
                <div style={{ padding: 10, background: "#222" }}>

                    {/* <button onClick={startRecording}>
                        Start Rec
                    </button>
                    <button onClick={stopRecording}>
                        Stop Rec
                    </button> */}

                    <input
                        type="range"
                        min="0"
                        max={duration}
                        step="0.01"
                        value={currentTime}
                        onChange={handleScrub}
                        style={{ background: '#ffcc33', width: "60%", marginLeft: 20 }}
                    />
                </div>
            )}
        </>
    );
});

export default VideoViewer;